import { FC, useEffect, useRef, useState } from 'react';
import Tabs from '../../common/Tabs/Tabs/Tabs';
import Tab from '../../common/Tabs/Tab/Tab';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import useCartModelDisplay from '../../../hooks/useCartModelDisplay';
import CartProducts from './CartProducts/CartProducts';
import CartInfoBar from './CartInfoBar/CartInfoBar';
import Product from '../../../@types/Product';
import { ChassisModelTypes } from '../../../@types/ChassisType';
import assets from '../../../assets';
import useAuth from '../../../hooks/useAuth';
import { useHref, useLocation } from 'react-router-dom';
import StickyBox from 'react-sticky-box';
import CartModelDisplay from '../../CartModelDisplay/CartModelDisplay';
import useCurrencyConverter from '../../../hooks/useCurrencyConverter';
import Select from '../../common/Select/Select';

interface CartBuilderProps {
  retailConfigurator: boolean;
  fromInventory: boolean;
  editingCart: boolean;
  hideAllPrices: boolean;
}

const CartBuilder: FC<CartBuilderProps> = ({ retailConfigurator, fromInventory, editingCart, hideAllPrices }) => {
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const { user } = useAuth();
  //TEMP remove hasLicenses to prevent LSV orders Per Erica by setting false
  const hasLicenses = false; //user?.companyAttributes?.licenses !== undefined && user?.companyAttributes?.licenses?.length > 0;
  const location = useLocation();
  const basename = useHref('/');
  const [modelWidth, setModelWidth] = useState<number>(1200);
  const [modelHeight, setModelHeight] = useState<number>(904);
  const [modelViewContainerHeight, setModelViewContainerHeight] = useState<number>(0);
  const modelViewRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<string>('power');
  const manualTabChange = useRef<boolean>(false);
  const powerSectionRef = useRef<HTMLDivElement>(null);
  const exteriorSectionRef = useRef<HTMLDivElement>(null);
  const interiorSectionRef = useRef<HTMLDivElement>(null);
  const accessoriesSectionRef = useRef<HTMLDivElement>(null);
  const lsvSectionRef = useRef<HTMLDivElement>(null);
  const summarySectionRef = useRef<HTMLDivElement>(null);
  const { setValue, getValues } = useFormContext();

  const [powerSectionVisible, setPowerSectionVisible] = useState<boolean>(false);
  const [exteriorSectionVisible, setExteriorSectionVisible] = useState<boolean>(false);
  const [interiorSectionVisible, setInteriorSectionVisible] = useState<boolean>(false);
  const [accessoriesSectionVisible, setAccessoriesSectionVisible] = useState<boolean>(false);
  const [lsvSectionVisible, setLsvSectionVisible] = useState<boolean>(false);
  const [summarySectionVisible, setSummarySectionVisible] = useState<boolean>(false);

  const { setCurrency, currencyOptions, currency, convertCurrency } = useCurrencyConverter();

  /*
    Handles adjusting header section tabs that are selected
    [Power, Exterior, Interior, Accessories, LSV, Summary]
    based on visibility.  
  */
  useEffect(() => {
    if (!manualTabChange.current) {
      // Set tabs disabled at top
      if (powerSectionVisible) {
        setTab('power');
      } else if (exteriorSectionVisible) {
        setTab('exterior');
      } else if (interiorSectionVisible) {
        setTab('interior');
      } else if (accessoriesSectionVisible) {
        setTab('accessories');
      } else if (lsvSectionVisible) {
        setTab('lsv');
      } else if (summarySectionVisible) {
        setTab('summary');
      }
    }
  }, [
    manualTabChange,
    powerSectionVisible,
    exteriorSectionVisible,
    interiorSectionVisible,
    accessoriesSectionVisible,
    lsvSectionVisible,
    summarySectionVisible
  ]);

  /*
    Handles detection on if head sections are visible
    [Power, Exterior, Interior, Accessories, Summary]
    This then is used to detect which tab to automatically
    adjust to visually
  */
  useEffect(() => {
    const observableParams: IntersectionObserverInit = {
      root: null, // viewport
      rootMargin: '0px', // no margin
      threshold: [0.05, 0.1] // 10% of target visible
    }

    // Power section
    const powerSectionObserver = new IntersectionObserver(
      ([entry]) => {
        setPowerSectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (powerSectionRef.current)
      powerSectionObserver.observe(powerSectionRef.current);

    // Exterior section
    const exteriorSectionObserver = new IntersectionObserver(
      ([entry]) => {
        setExteriorSectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (exteriorSectionRef.current)
      exteriorSectionObserver.observe(exteriorSectionRef.current);

    // Interior section
    const interiorSectionObserver = new IntersectionObserver(
      ([entry]) => {
        setInteriorSectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (interiorSectionRef.current)
      interiorSectionObserver.observe(interiorSectionRef.current);

    // Accessories section
    const accessoriesSectionObserver = new IntersectionObserver(
      ([entry]) => {
        setAccessoriesSectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (accessoriesSectionRef.current)
      accessoriesSectionObserver.observe(accessoriesSectionRef.current);

    // Lsv section
    const lsvSectionObserver = new IntersectionObserver(
      ([entry]) => {
        setLsvSectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (lsvSectionRef.current)
      lsvSectionObserver.observe(lsvSectionRef.current);

    // Summary section
    const summarySectionObserver = new IntersectionObserver(
      ([entry]) => {
        setSummarySectionVisible(entry.isIntersecting);
      }, observableParams
    );

    if (summarySectionRef.current)
      summarySectionObserver.observe(summarySectionRef.current);


    // Clean up the observer
    return () => {
      if (powerSectionRef.current)
        powerSectionObserver.unobserve(powerSectionRef.current);

      if (exteriorSectionRef.current)
        exteriorSectionObserver.unobserve(exteriorSectionRef.current);

      if (interiorSectionRef.current)
        interiorSectionObserver.unobserve(interiorSectionRef.current);

      if (accessoriesSectionRef.current)
        accessoriesSectionObserver.unobserve(accessoriesSectionRef.current);

      if (lsvSectionRef.current)
        lsvSectionObserver.unobserve(lsvSectionRef.current);

      if (summarySectionRef.current)
        summarySectionObserver.unobserve(summarySectionRef.current);
    };
  }, []);

  // Handles change of tabs at the top along with
  // auto scrolling to the next section
  const handleTabChange = async (event: any, tab: string) => {
    // Set manualTabChange ref to current
    // this prevents the checkInView function from
    // firing when it shouldn't
    manualTabChange.current = true;
    setTab(tab);
    scrollToSection(tab);

    // Need to reset the manualTabChange value so that way the 
    // onScroll function (checkInView) works properly again
    setTimeout(() => manualTabChange.current = false, 3000);
  };

  const scrollIntoView = (element: HTMLDivElement | undefined) => {
    if (autoScroll && element) {
      const position = element.getBoundingClientRect();
      const offset = isLaptop || isTablet || isMobile ? modelViewContainerHeight + 15 : 15;
      const offsetDealerPortal = (isLaptop || isTablet || isMobile ? modelViewContainerHeight + 120 : 120);
      if (retailConfigurator) {
        window.scrollTo({
          behavior: 'smooth',
          top: position.top + window.scrollY - offset
        });
      } else {
        const modalContent = document.getElementById('modalContent');
        modalContent?.scrollTo({
          behavior: 'smooth',
          top: position.top +
            modalContent.scrollTop -
            offsetDealerPortal
        });

      }
    }
  }

  const scrollToSection = (tab: string) => {
    // Tab should match the ID of the parent container we
    // want to scroll to
    switch (tab) {
      case 'power':
        if (powerSectionRef.current)
          scrollIntoView(powerSectionRef.current);
        break;
      case 'exterior':
        if (exteriorSectionRef.current)
          scrollIntoView(exteriorSectionRef.current);
        break;
      case 'interior':
        if (interiorSectionRef.current)
          scrollIntoView(interiorSectionRef.current);
        break;
      case 'accessories':
        if (accessoriesSectionRef.current)
          scrollIntoView(accessoriesSectionRef.current);
        break;
      case 'lsv':
        if (lsvSectionRef.current)
          scrollIntoView(lsvSectionRef.current);
        break;
      case 'summary':
        if (summarySectionRef.current)
          scrollIntoView(summarySectionRef.current);
        break;
    }
  }

  // Calculate total for the cart configuration & qty
  const { chassisType } = useCartModelDisplay();
  const chassisTypeSku = useWatch({ name: 'chassisTypeSku' });
  const chassisTypePrice = useWatch({ name: 'chassisTypePrice' });
  const bodyColor: Product | undefined = useWatch({ name: 'bodyColor' });
  const frontSeat = useWatch({ name: 'frontSeat' });
  const middleSeat = useWatch({ name: 'middleSeat' });
  const rearSeat = useWatch({ name: 'rearSeat' });
  const seatFrame = useWatch({ name: 'seatFrame' });
  const liftKit = useWatch({ name: 'liftKit' });
  const chassisBatteryConfig = useWatch({ name: 'chassisBatteryConfig' });
  const chassisBattery = useWatch({ name: 'chassisBattery' });
  const wheel = useWatch({ name: 'wheel' });
  const tire = useWatch({ name: 'tire' });
  const lsvKit = useWatch({ name: 'lsvKit' });
  const lugNut = useWatch({ name: 'lugNut' });
  const top = useWatch({ name: 'top' });
  const windshield = useWatch({ name: 'windshield' });
  const quantity = useWatch({ name: 'quantity' });
  const optionalAccessories: Product[] | undefined = useWatch({ name: 'optionalAccessories' });
  const registrationMark = String.fromCodePoint(0x000AE);

  useEffect(() => {
    let sumOfAccessoryProducts = 0;
    optionalAccessories?.forEach((acc: Product) =>
      sumOfAccessoryProducts +=
      retailConfigurator
        ? acc.retailPrice ?? 0
        : acc.price ?? 0);
    const sumOfProducts =
      (chassisTypePrice ?? 0) +
      (retailConfigurator ? bodyColor?.retailPrice ?? 0 : bodyColor?.price ?? 0) +
      (retailConfigurator ? frontSeat?.retailPrice ?? 0 : frontSeat?.price ?? 0) +
      (retailConfigurator ? middleSeat?.retailPrice ?? 0 : middleSeat?.price ?? 0) +
      (retailConfigurator ? rearSeat?.retailPrice ?? 0 : rearSeat?.price ?? 0) +
      (retailConfigurator ? seatFrame?.retailPrice ?? 0 : seatFrame?.price ?? 0) +
      (retailConfigurator ? liftKit?.retailPrice ?? 0 : liftKit?.price ?? 0) +
      (retailConfigurator ? chassisBatteryConfig?.retailPrice ?? 0 : chassisBatteryConfig?.price ?? 0) +
      (retailConfigurator ? chassisBattery?.retailPrice ?? 0 : chassisBattery?.price ?? 0) +
      (retailConfigurator ? wheel?.retailPrice ?? 0 : wheel?.price ?? 0) +
      (retailConfigurator ? tire?.retailPrice ?? 0 : tire?.price ?? 0) +
      (retailConfigurator ? lsvKit?.retailPrice ?? 0 : lsvKit?.price ?? 0) +
      (retailConfigurator ? lugNut?.retailPrice ?? 0 : lugNut?.price ?? 0) +
      (retailConfigurator ? windshield?.retailPrice ?? 0 : windshield?.price ?? 0) +
      (retailConfigurator ? top?.retailPrice ?? 0 : top?.price ?? 0) +
      (sumOfAccessoryProducts);
    setValue('cartPrice', sumOfProducts);
    setValue('total', (sumOfProducts * (quantity ?? 1)));

  }, [
    retailConfigurator,
    chassisTypeSku,
    chassisTypePrice,
    bodyColor,
    frontSeat,
    middleSeat,
    rearSeat,
    seatFrame,
    liftKit,
    liftKit,
    chassisBatteryConfig,
    chassisBattery,
    wheel,
    lsvKit,
    tire,
    lugNut,
    windshield,
    top,
    quantity,
    optionalAccessories,
    setValue
  ]);

  /* 
    Generate the chassisSku &
  */
  useEffect(() => {
    const sku = [
      chassisBatteryConfig?.sku?.split('-', 2).join('-'),
      bodyColor?.sku,
      liftKit?.sku !== 'NL'
        ? liftKit?.sku
        : null,
      lsvKit && lsvKit?.sku !== 'None'
        ? 'LSV'
        : null
    ];

    // Set various default products if no defaults are set already
    setValue('model', chassisType?.model, { shouldDirty: false });
    setValue('year', chassisType?.year, { shouldDirty: false });
    setValue('chassisSku', sku.filter(Boolean).join('-'), { shouldDirty: false });
    setValue('chassisType', `${chassisType?.model} (${chassisType?.name})`, { shouldDirty: false });
    setValue('chassisTypePrice', retailConfigurator ? chassisType?.retailPrice : chassisType?.price, { shouldDirty: false });
    setValue('chassisTypeSku', chassisType?.sku, { shouldDirty: false });
    setValue('top', chassisType?.topOptions[0], { shouldDirty: false });

    if (getValues('top') === undefined) {
      setValue('top', chassisType?.topOptions[0], { shouldDirty: false });
    }

    if (getValues('windshield') === undefined) {
      setValue('windshield', chassisType?.windshieldOptions[0], { shouldDirty: false });
    }

    if (retailConfigurator && getValues('lsvKit') === undefined) {
      setValue('lsvKit', chassisType?.lsvKitOptions?.find((product) => product.sku === 'None'), { shouldDirty: false });
    } else {
      if (user && getValues('lsvKit') === undefined) {
        setValue('lsvKit', chassisType?.lsvKitOptions?.find((product) => product.sku === 'None'), { shouldDirty: false });
      }
    }
  }, [
    retailConfigurator,
    user,
    chassisType,
    bodyColor,
    chassisBatteryConfig,
    liftKit,
    lsvKit,
    setValue
  ]);

  // Generate buildUrlParam field
  useEffect(() => {
    /* 
        Get available url query params to generate build
          keys: 
          bc = bodyColor.sku
          lk = liftKit.sku
          fs = frontSeat.sku
          wh = wheel.sku
          cb = chassisBatteryConfig.sku
          oa = optionalAccessoriesSku Array
          optionalAccessories are read from an array comma separated\
          example url: /X6?bc=BL02&lk=L&fs=1&wh=1&cb=27-035&oa=19,21,22,24,28
      */

    let urlParams: string[] = [];

    urlParams.push(`bc=${bodyColor?.sku}`);

    if (liftKit)
      urlParams.push(`lk=${liftKit.sku}`);

    if (frontSeat)
      urlParams.push(`fs=${frontSeat.sku}`);

    if (wheel)
      urlParams.push(`wh=${wheel.sku}`);

    if (chassisBatteryConfig)
      urlParams.push(`cb=${chassisBatteryConfig.sku}`);

    let accessoriesSkuList: string[] = [];
    for (const optionalAccessory of optionalAccessories ?? []) {
      accessoriesSkuList.push(optionalAccessory.sku);
    }

    if (accessoriesSkuList.length)
      urlParams.push(`oa=${accessoriesSkuList.join(',')}`);

    if (retailConfigurator) {
      const urlBuildParams = `${process.env.REACT_APP_BASEURL}${basename}${location.pathname}?${urlParams.join('&')}`;
      setValue('urlBuildParams', urlBuildParams);
    }

  }, [
    retailConfigurator,
    bodyColor,
    liftKit,
    frontSeat,
    wheel,
    chassisBatteryConfig,
    optionalAccessories,
    location,
    basename,
    setValue
  ]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));
  const isLaptopXL = useMediaQuery(theme.breakpoints.between('laptopXL', 'desktop'));
  const isLaptopL = useMediaQuery(theme.breakpoints.between('laptopL', 'laptopXL'));
  const isLaptop = useMediaQuery(theme.breakpoints.between('laptop', 'laptopL'));
  const isTablet = useMediaQuery(theme.breakpoints.between('tabletS', 'laptop'));
  const isMobile = useMediaQuery(theme.breakpoints.down('tabletS'));

  useEffect(() => {
    if (isDesktop) {
      if (retailConfigurator) {
        setModelWidth(1200);
        setModelHeight(844);
      } else {
        setModelWidth(850);
        setModelHeight(598);
      }
    }

    if (isLaptopXL) {
      if (retailConfigurator) {
        setModelWidth(1080);
        setModelHeight(760);
      } else {
        setModelWidth(850);
        setModelHeight(598);
      }
    }

    if (isLaptopL) {
      setModelWidth(850);
      setModelHeight(598);
    }

    if (isLaptop) {
      setModelWidth(650);
      setModelHeight(457);
    }

    if (isTablet) {
      setModelWidth(500);
      setModelHeight(352);
    }

    if (isMobile) {
      setModelWidth(360);
      setModelHeight(253);
    }

  }, [
    retailConfigurator,
    isDesktop,
    isLaptopXL,
    isLaptopL,
    isLaptop,
    isTablet,
    isMobile,
    setModelWidth,
    setModelHeight
  ]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      setModelViewContainerHeight(event[0].contentBoxSize[0].blockSize);
    });

    if (modelViewRef && modelViewRef.current) {
      resizeObserver.observe(modelViewRef.current);
    }
  }, [
    modelViewRef,
    setModelViewContainerHeight
  ]);

  return (
    <Stack direction="column">
      {retailConfigurator &&
        <Stack direction="column">
          <Stack
            sx={{
              minHeight: '200px',
              backgroundColor: '#000000',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiTypography-root': {
                color: 'white'
              }
            }}>
            <Stack
              direction={
                isTablet || isMobile
                  ? 'column'
                  : 'row'
              }
              spacing={6}
              justifyContent='space-evenly'
              alignItems={
                isTablet || isMobile
                  ? 'center'
                  : 'flex-start'}
              sx={(theme) => ({
                padding: 12,
                [theme.breakpoints.down('tabletS')]: {
                  paddingLeft: '8px',
                  paddingRight: '8px'
                }
              })}>
              <Box width="250px">
                {chassisType?.model !== ChassisModelTypes.E4
                  ? (<img src={assets.images.madjaxXSeriesLogoWhite} alt="xseries-logo" />)
                  : (<img src={assets.images.madjaxESeriesLogoWhite} alt="xseries-logo" />)
                }
              </Box>
              <Stack direction="column"
                sx={{
                  textAlign: `
                    ${isTablet || isMobile
                      ? 'center'
                      : 'left'}`
                }}>
                <Typography
                  variant='primaryFont'
                  fontSize={32}
                  fontWeight="bold"
                  textTransform="uppercase">
                  MadJax{registrationMark} {chassisType?.categoryName}{registrationMark} {chassisType?.model} {chassisType?.year} {chassisType?.passengerCapacity}-Passenger
                </Typography>
                <Typography
                  variant="primaryFont"
                  fontSize={18}>
                  Build, Save, and Share Your MadJax Vehicle
                </Typography>
              </Stack>
              <Stack
                direction="column"
                sx={{
                  alignItems: `${isTablet || isMobile
                    ? 'center'
                    : 'flex-end'}`,
                  textAlign: `${isTablet || isMobile
                    ? 'center'
                    : 'right'}`,
                  '& .MuiFormControl-root': {
                    marginTop: '8px',
                    '& .MuiInputBase-root': {
                      color: 'white',
                      minWidth: 'fit-content',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderImage: 'linear-gradient(#c2171d, #ff9e2d) 30%;',
                        borderWidth: '2px'
                      }
                    }
                  }
                }}>
                {!hideAllPrices &&
                  <Typography
                    variant="primaryFont"
                    fontSize={32}
                    fontWeight="bold">
                    Starting at {convertCurrency(chassisType?.retailPrice ?? 0)}
                  </Typography>
                }
                {!hideAllPrices &&
                  <Select
                    label={'Currency'}
                    value={currency}
                    options={currencyOptions}
                    autoWidth={false}
                    multiple={false}
                    native={false}
                    onChange={(event) => setCurrency(String(event.target.value))} />
                }
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      }
      <Stack
        direction={isLaptop || isTablet || isMobile ? 'column' : 'row'}
        alignItems="flex-start"
        sx={{
          backgroundColor: '#FFF',
          zIndex: 1031
        }}>
        <StickyBox style={{
          width: '100%',
          backgroundColor: '#FFF',
          zIndex: 1032
        }} offsetTop={0}>
          <Stack
            ref={modelViewRef}
            direction="column"
            paddingTop="10px"
            paddingLeft="10px"
            paddingRight="10px"
            sx={(theme) => ({
              ...((isLaptop || isTablet || isMobile) && {
                padding: '2px'
              }),
            })}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  fontSize: '18px',
                  letterSpacing: '4px',
                  fontFamily: [
                    "Montserrat-Variable",
                    "Helvetica",
                    "Arial",
                    "sans-serif"
                  ].join(','),
                }
              }}>
              <Tab label="power" value="power" />
              <Tab label="exterior" value="exterior" />
              <Tab label="interior" value="interior" />
              {retailConfigurator &&
                <Tab label="accessories" value="accessories" />
              }
              {(!retailConfigurator && hasLicenses) &&
                <Tab label="lsv" value="lsv" />
              }
              <Tab label="summary" value="summary" />

            </Tabs>
            <CartModelDisplay
              width={modelWidth}
              height={modelHeight}
              alignVertical={true}
              alignHorizontal={true} />
          </Stack>
        </StickyBox>
        <CartProducts
          modelViewHeight={modelViewContainerHeight}
          editingCart={editingCart}
          autoScroll={autoScroll}
          retailConfigurator={retailConfigurator}
          powerSectionRef={powerSectionRef}
          exteriorSectionRef={exteriorSectionRef}
          interiorSectionRef={interiorSectionRef}
          accessoriesSectionRef={accessoriesSectionRef}
          lsvSectionRef={lsvSectionRef}
          summarySectionRef={summarySectionRef}
          fromInventory={fromInventory}
          hideAllPrices={hideAllPrices} />
        {retailConfigurator &&
          <CartInfoBar hideAllPrices={hideAllPrices} />
        }
      </Stack>
    </Stack>
  );
}

export default CartBuilder;
